"use client";

/**
 * Personal Skills — the skills a user has added (cloned) to their library.
 *
 * Two modes:
 *   - LOGGED IN  → real backend. `/alerts/skills/mine` lists the user's clones;
 *     Add clones a public skill (`POST /alerts/skills/{id}/clone`), Remove
 *     deletes the clone (`DELETE /alerts/skills/{id}`). Identity is keyed by the
 *     PUBLIC source id (`cloned_from`) so the sidebar + detail page always
 *     resolve through the public catalog (clones are frozen copies anyway).
 *   - LOGGED OUT → localStorage mock (the original demo behavior) so the
 *     sidebar/skills surfaces still work for visitors browsing the marketing UI.
 *
 * The `usePersonalSkills()` API is unchanged so every consumer (sidebar,
 * chat-bar modal, detail page Add button) keeps working without edits.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { MOCK_SKILLS, type Skill } from "@/lib/mock/skills";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient, type PublicSkill } from "@/lib/api-client";
import { prettySource, cleanInstruction } from "@/lib/skills-map";

const STORAGE_KEY = "xynth:personal-skills";

function seedIds(): string[] {
  return MOCK_SKILLS.filter((s) => s.added_to_personal || s.undeletable).map(
    (s) => s.id,
  );
}

/** Map a backend clone (PublicSkill) into the sidebar's Skill shape. We key the
 * id by the public source (`cloned_from`) so links resolve via the catalog. */
function cloneToSkill(s: PublicSkill): Skill {
  const explanation = s.explanation || "";
  return {
    id: s.cloned_from || s.id,
    name: s.name,
    description: cleanInstruction(explanation || s.agent_instruction || "").slice(
      0,
      160,
    ),
    explanation_md: explanation || s.agent_instruction || "",
    raw_md: s.agent_instruction || "",
    author: "Xynth",
    is_public: false,
    output_type: "research",
    data_sources: (s.data_sources || []).map((d) => ({
      id: d,
      name: prettySource(d),
      category: "",
    })),
    created_at: s.created_at || new Date().toISOString(),
    usage_count: s.usage_count || 0,
    added_to_personal: true,
    personal_action: s.cloned_from ? "unadd" : "delete",
    cloned_from: s.cloned_from || null,
  };
}

interface PersonalSkillsValue {
  skills: Skill[];
  isAdded: (id: string) => boolean;
  add: (id: string) => void;
  remove: (id: string) => void;
  toggle: (id: string) => void;
  /** Re-pull the user's personal skills from the backend. Call after a skill
   *  is created so the sidebar/library reflect it immediately. No-op when
   *  logged out. */
  refresh: () => Promise<void>;
  /** Optimistically drop a just-created skill into the store so it appears in
   *  the sidebar/modal INSTANTLY (before the refetch round-trip). A following
   *  refresh() reconciles against the backend; same id → no duplicate. */
  addCreated: (s: PublicSkill) => void;
}

const PersonalSkillsContext = createContext<PersonalSkillsValue | null>(null);

export function PersonalSkillsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  const loggedIn = Boolean(user);

  // ── LOGGED-OUT mock store (localStorage) ──────────────────────────────────
  const [ids, setIds] = useState<string[]>(seedIds);
  // ── LOGGED-IN real store (clones from the backend) ────────────────────────
  const [clones, setClones] = useState<PublicSkill[]>([]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as string[];
        if (Array.isArray(parsed)) {
          const forced = MOCK_SKILLS.filter((s) => s.undeletable).map(
            (s) => s.id,
          );
          setIds(Array.from(new Set([...forced, ...parsed])));
        }
      }
    } catch {
      // ignore corrupt storage
    }
  }, []);

  const refetchMine = useCallback(async () => {
    if (!loggedIn) return;
    try {
      const res = await apiClient.listMySkills();
      setClones(res.skills || []);
    } catch {
      // keep whatever we have; never crash the app shell
    }
  }, [loggedIn]);

  // Load the user's clones whenever they log in (and clear on logout).
  useEffect(() => {
    if (loggedIn) {
      void refetchMine();
    } else {
      setClones([]);
    }
  }, [loggedIn, refetchMine]);

  const persist = useCallback((next: string[]) => {
    setIds(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore quota / private-mode errors
    }
  }, []);

  // A clone matches a public id by cloned_from, or directly by its own id.
  const cloneFor = useCallback(
    (id: string) =>
      clones.find((c) => c.cloned_from === id || c.id === id),
    [clones],
  );

  const isAdded = useCallback(
    (id: string) => (loggedIn ? Boolean(cloneFor(id)) : ids.includes(id)),
    [loggedIn, cloneFor, ids],
  );

  const add = useCallback(
    (id: string) => {
      // eslint-disable-next-line no-console
      console.log("[SKILL-LAUNCH] add skill click", { id, loggedIn });
      if (loggedIn) {
        if (cloneFor(id)) return; // already added
        // Optimistic: clone server-side, then reconcile from the source list.
        apiClient
          .cloneSkill(id)
          .then(() => {
            // eslint-disable-next-line no-console
            console.log("[SKILL-LAUNCH] cloneSkill OK", { id });
            refetchMine();
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.log("[SKILL-LAUNCH] cloneSkill failed", { id, err });
            refetchMine();
          });
        return;
      }
      if (ids.includes(id)) return;
      persist([...ids, id]);
    },
    [loggedIn, cloneFor, refetchMine, ids, persist],
  );

  const remove = useCallback(
    (id: string) => {
      if (loggedIn) {
        const c = cloneFor(id);
        if (!c) return;
        // Optimistic removal, then reconcile.
        setClones((prev) => prev.filter((x) => x.id !== c.id));
        apiClient
          .deleteSkill(c.id)
          .then(() => refetchMine())
          .catch(() => refetchMine());
        return;
      }
      const skill = MOCK_SKILLS.find((s) => s.id === id);
      if (skill?.undeletable) return;
      persist(ids.filter((x) => x !== id));
    },
    [loggedIn, cloneFor, refetchMine, ids, persist],
  );

  const toggle = useCallback(
    (id: string) => {
      if (isAdded(id)) remove(id);
      else add(id);
    },
    [isAdded, add, remove],
  );

  // Optimistic insert: prepend a freshly-created skill (deduped by id) so the
  // sidebar/modal show it the instant Save succeeds, before refetchMine lands.
  const addCreated = useCallback((s: PublicSkill) => {
    if (!s?.id) return;
    setClones((prev) =>
      prev.some((c) => c.id === s.id) ? prev : [s, ...prev],
    );
  }, []);

  const skills = useMemo(() => {
    if (loggedIn) return clones.map(cloneToSkill);
    return ids
      .map((id) => MOCK_SKILLS.find((s) => s.id === id))
      .filter((s): s is Skill => Boolean(s));
  }, [loggedIn, clones, ids]);

  const value = useMemo<PersonalSkillsValue>(
    () => ({
      skills,
      isAdded,
      add,
      remove,
      toggle,
      refresh: refetchMine,
      addCreated,
    }),
    [skills, isAdded, add, remove, toggle, refetchMine, addCreated],
  );

  return (
    <PersonalSkillsContext.Provider value={value}>
      {children}
    </PersonalSkillsContext.Provider>
  );
}

export function usePersonalSkills(): PersonalSkillsValue {
  const ctx = useContext(PersonalSkillsContext);
  if (!ctx) {
    throw new Error(
      "usePersonalSkills must be used within a PersonalSkillsProvider",
    );
  }
  return ctx;
}
